import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, FileText, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  fetchMaterialidadResumen,
  materialidadCartaClientePdfUrl,
  materialidadCartaClienteXlsxUrl,
  materialidadResumenPdfUrl,
} from "@/modules/tramites/materialidad/materialidadApi";
import MaterialidadPeriodFilter from "@/modules/tramites/materialidad/MaterialidadPeriodFilter";
import {
  buildPeriodQuery,
  defaultPeriodSelection,
  formatPeriodSelectionLabel,
} from "@/modules/tramites/materialidad/materialidadConstants";

/**
 * Resumen por empresa — indicadores preventivos (sin dictamen legal).
 */
export default function MaterialidadResumenPanel({ clientId }) {
  const [periodSelection, setPeriodSelection] = useState(defaultPeriodSelection);
  const [loading, setLoading] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [riskFilter, setRiskFilter] = useState("all");

  const periodQuery = useMemo(() => buildPeriodQuery(periodSelection), [periodSelection]);

  const loadResumen = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMaterialidadResumen({
        ...periodQuery,
        clientId: clientId || undefined,
      });
      setResumen(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar el resumen.");
      setResumen(null);
    } finally {
      setLoading(false);
    }
  }, [periodQuery, clientId]);

  useEffect(() => {
    loadResumen();
  }, [loadResumen]);

  const allRows = resumen?.rows || [];
  const totals = resumen?.totals || {};
  const periodLabel = resumen?.period_label || formatPeriodSelectionLabel(periodSelection);

  const rows = useMemo(() => {
    if (riskFilter === "criticos") {
      return allRows.filter((r) => Number(r.criticos || 0) > 0);
    }
    if (riskFilter === "alto") {
      return allRows.filter((r) => Number(r.riesgo_alto || 0) > 0);
    }
    if (riskFilter === "faltantes") {
      return allRows.filter((r) => Number(r.faltantes || 0) > 0);
    }
    return allRows;
  }, [allRows, riskFilter]);

  const cards = [
    { key: "faltantes", label: "Faltantes", value: totals.faltantes ?? "—", tone: "text-amber-800" },
    { key: "criticos", label: "Críticos", value: totals.criticos ?? "—", tone: "text-rose-800" },
    { key: "cancelados", label: "CFDI cancelados", value: totals.cfdi_cancelados ?? "—", tone: "text-rose-700" },
    { key: "efos", label: "Alertas EFOS", value: totals.efos_alertas ?? "—", tone: "text-rose-700" },
    { key: "solicitados", label: "Solicitados", value: totals.solicitados ?? "—", tone: "text-sky-800" },
    { key: "auto", label: "Auto-detectados", value: totals.auto_detectados ?? "—", tone: "text-slate-800" },
  ];

  return (
    <div className="space-y-4" data-testid="materialidad-resumen-panel">
      <div className="rounded-md border border-slate-200 bg-white px-3 py-2.5 shadow-sm space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <MaterialidadPeriodFilter
            idPrefix="materialidad-resumen"
            value={periodSelection}
            onChange={setPeriodSelection}
            compact
          />
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500 shrink-0" htmlFor="materialidad-resumen-risk-filter">
              Filtrar
            </label>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger id="materialidad-resumen-risk-filter" className="rounded-sm w-[180px] h-8" data-testid="materialidad-resumen-risk-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="criticos">Con hallazgo crítico</SelectItem>
                <SelectItem value="alto">Con riesgo alto</SelectItem>
                <SelectItem value="faltantes">Con faltantes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
          <div className="flex-1 min-w-[160px] text-xs text-slate-600">
            Resumen · {periodLabel}
            {clientId ? " · empresa seleccionada" : " · todas las empresas"}
            <span className="block text-[10px] text-slate-500">
              Indicador preventivo — no es dictamen de materialidad fiscal.
            </span>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button type="button" variant="outline" size="sm" className="rounded-sm h-8" disabled={loading} onClick={loadResumen}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Actualizar
            </Button>
            <Button type="button" variant="outline" size="sm" className="rounded-sm h-8" asChild>
              <a
                href={materialidadResumenPdfUrl({ ...periodQuery, clientId: clientId || undefined })}
                download
                data-testid="materialidad-download-resumen-pdf"
              >
                <Download className="h-4 w-4 mr-1" />
                PDF resumen
              </a>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm h-8"
              disabled={!clientId}
              asChild={Boolean(clientId)}
              data-testid="materialidad-download-carta-cliente-resumen"
            >
              {clientId ? (
                <a href={materialidadCartaClientePdfUrl({ ...periodQuery, clientId })} download>
                  <FileText className="h-4 w-4 mr-1" />
                  Carta PDF
                </a>
              ) : (
                <span>
                  <FileText className="h-4 w-4 mr-1 inline" />
                  Carta PDF
                </span>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm h-8"
              disabled={!clientId}
              asChild={Boolean(clientId)}
              data-testid="materialidad-download-carta-cliente-excel-resumen"
            >
              {clientId ? (
                <a href={materialidadCartaClienteXlsxUrl({ ...periodQuery, clientId })} download>
                  Excel
                </a>
              ) : (
                <span>Excel</span>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="materialidad-resumen-totals">
        {cards.map((card) => (
          <div
            key={card.key}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm"
          >
            <p className="text-[10px] uppercase tracking-wide text-slate-500 m-0">{card.label}</p>
            <p className={`text-lg font-semibold m-0 ${card.tone}`}>{card.value}</p>
          </div>
        ))}
      </div>

      <section className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando resumen…
          </div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-slate-600 m-0">Sin datos de materialidad para este periodo.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 font-semibold">Empresa</th>
                  <th className="px-3 py-2 font-semibold">Ítems</th>
                  <th className="px-3 py-2 font-semibold">Faltantes</th>
                  <th className="px-3 py-2 font-semibold">Completos</th>
                  <th className="px-3 py-2 font-semibold">Críticos</th>
                  <th className="px-3 py-2 font-semibold">Cancelados</th>
                  <th className="px-3 py-2 font-semibold">EFOS</th>
                  <th className="px-3 py-2 font-semibold">Solicitados</th>
                  <th className="px-3 py-2 font-semibold">Riesgo alto</th>
                  <th className="px-3 py-2 font-semibold text-right">Carta cliente</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.client_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-900">{row.client_name}</td>
                    <td className="px-3 py-2">{row.total_items}</td>
                    <td className="px-3 py-2 text-amber-800">{row.faltantes}</td>
                    <td className="px-3 py-2 text-emerald-700">{row.completos}</td>
                    <td className="px-3 py-2 text-rose-800 font-semibold">{row.criticos || 0}</td>
                    <td className="px-3 py-2 text-rose-700">{row.cfdi_cancelados || 0}</td>
                    <td className="px-3 py-2 text-rose-700">{row.efos_alertas || 0}</td>
                    <td className="px-3 py-2 text-sky-800">{row.solicitados || 0}</td>
                    <td className="px-3 py-2 text-rose-700 font-semibold">{row.riesgo_alto}</td>
                    <td className="px-3 py-2 text-right">
                      <Button type="button" variant="outline" size="sm" className="rounded-sm h-8 text-xs" asChild>
                        <a
                          href={materialidadCartaClientePdfUrl({ ...periodQuery, clientId: row.client_id })}
                          download
                          data-testid={`materialidad-download-carta-cliente-${row.client_id}`}
                        >
                          <FileText className="h-3.5 w-3.5 mr-1" />
                          Descargar
                        </a>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
